import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
	Agent,
	AgentAccountTopUpValues,
	AgentInfo,
	AgentState,
	AgentTransaction,
	AgentValues,
	FloatRequest,
} from '../../pages/agent-management/types';
import { RootState } from '../store';
import agentService from '../../API/agentApi';
import { toast } from 'react-toastify';

const initialState: AgentState = {
	data: [],
	agentTranscation: [],
	agentTransactionPagination: undefined,
	floatRequest: [],
	agent: null,
	isLoading: false,
};

export const asyncRetrieveAgents = createAsyncThunk<
	Agent[],
	void,
	{ state: RootState; rejectValue: string }
>('agents/retrieve', async (_, thunkApi) => {
	try {
		const res = await agentService.retrieveAgent();
		console.log("agentss got", res)
		return res.data.data;
	} catch (error) {
		const errorMessage =
			(error.res && error.res.data && error.res.data.message && error.message) ||
			error.toString();
		toast.error(errorMessage);
		return thunkApi.rejectWithValue(errorMessage);
	}
});

export const asyncRetrieveAgentTransactions = createAsyncThunk<
	{ data: AgentTransaction[]; pagination: any },
	{ id: string; page?: number; limit?: number },
	{ state: RootState; rejectValue: string }
>('agentTransactions/retrieve', async ({ id, page = 1, limit = 10 }, thunkApi) => {
	try {
		const res = await agentService.retrieveAgetTranscations(id, page, limit);
		// console.log('transcation res', res);
		return { data: res.data.data, pagination: res.data.pagination };
	} catch (error) {
		const errorMessage = (error.response?.data?.message ?? error.message) || error.toString();
		toast.error(errorMessage);
		return thunkApi.rejectWithValue(errorMessage);
	}
});

export const asyncRetrieveAgentFloatRequest = createAsyncThunk<
	FloatRequest[],
	string,
	{ state: RootState; rejectValue: string }
>('agentFloatRequest/retrieve', async (id, thunkApi) => {
	try {
		const res = await agentService.retrieveAgentFloatRequests(id);
		console.log('transcation res', res);
		return res.data.data || [];
	} catch (error) {
		const errorMessage = (error.response?.data?.message ?? error.message) || error.toString();
		toast.error(errorMessage);
		return thunkApi.rejectWithValue(errorMessage);
	}
});

export const asyncAgentAccountTopUp = createAsyncThunk<
	{ id: string; payload: Partial<AgentAccountTopUpValues> },
	{ id: string; payload: Partial<AgentAccountTopUpValues> },
	{ state: RootState; rejectValue: string }
>('agentTopUp/retrieve', async ({ id, payload }, thunkApi) => {
	try {
		const res = await agentService.topUpAgentAccount(id, payload);
		toast.success(res.data.message);
		payload;
		return res.data.data || [];
	} catch (error) {
		const errorMessage = (error.response?.data?.message ?? error.message) || error.toString();
		toast.error(errorMessage);
		return thunkApi.rejectWithValue(errorMessage);
	}
});

export const asyncRegisterAgent = createAsyncThunk<
	Agent,
	{ payload: AgentValues; token: string },
	{ rejectValue: string }
>('agent/register', async ({ payload, token }, thunkApi) => {
	try {
		const res = await agentService.createAgent(payload, token);
		toast.success(res.data.message || 'Agent Registered successfully');
		return res.data.data;
	} catch (error: any) {
		const errorMessage =
			(error.response && error.response.data && error.response.data.message) ||
			error.toString();
		toast.error(errorMessage);
		return thunkApi.rejectWithValue(errorMessage);
	}
});

export const asyncDeactivateAgent = createAsyncThunk<
	string,
	string,
	{ string: RootState; rejectValue: string }
>('agnet/delete', async (id, thunkApi) => {
	try {
		const res = await agentService.deactivate(id);
		toast.success(res.data.message);
		return id;
	} catch (error) {
		const errorMessage =
			(error.res && error.res.data && error.res.data.message && error.message) ||
			error.toString();
		toast.error(errorMessage);
		return thunkApi.rejectWithValue(errorMessage);
	}
});

export const asyncActivateAgent = createAsyncThunk<
	string,
	string,
	{ string: RootState; rejectValue: string }
>('agent/activate', async (id, thunkApi) => {
	try {
		const res = await agentService.activate(id);
		toast.success(res.data.message);
		return id;
	} catch (error) {
		const errorMessage =
			(error.res && error.res.data && error.res.data.message && error.message) ||
			error.toString();
		toast.error(errorMessage);
		return thunkApi.rejectWithValue(errorMessage);
	}
});

export const asyncRetrieveAgent = createAsyncThunk<
	AgentInfo,
	string,
	{ string: RootState; rejectValue: string }
>('agent/singleAgent', async (id, thunkApi) => {
	try {
		const res = await agentService.retrieveSingleAgent(id);
		return res.data.data;
	} catch (error) {
		const errorMessage =
			(error.res && error.res.data && error.res.data.message && error.message) ||
			error.toString();
		toast.error(errorMessage);
		return thunkApi.rejectWithValue(errorMessage);
	}
});

const agentSlice = createSlice({
	name: 'agents',
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(asyncRetrieveAgents.pending, (state) => {
				state.isLoading = true;
			})
			.addCase(asyncRetrieveAgents.fulfilled, (state, action: PayloadAction<Agent[]>) => {
				state.data = action.payload;
				state.isLoading = false;
			})
			.addCase(asyncRetrieveAgents.rejected, (state) => {
				state.isLoading = false;
			})
			.addCase(asyncRegisterAgent.pending, (state) => {
				state.isLoading = false;
			})
			.addCase(asyncRegisterAgent.fulfilled, (state, action: PayloadAction<Agent>) => {
				state.isLoading = false;
				state.data = [action.payload, ...state.data];
			})
			.addCase(asyncRegisterAgent.rejected, (state) => {
				state.isLoading = false;
			})
			.addCase(asyncDeactivateAgent.pending, (state) => {
				state.isLoading = false;
			})
			.addCase(asyncDeactivateAgent.fulfilled, (state, action) => {
				// Update the agent status instead of removing it
				state.data = state.data.map((agent) => 
					agent.id === action.meta.arg 
						? { ...agent, status: 'inactive' } 
						: agent
				);
			})
			.addCase(asyncDeactivateAgent.rejected, (state) => {
				state.isLoading = false;
			})
			.addCase(asyncActivateAgent.pending, (state) => {
				state.isLoading = false;
			})
			.addCase(asyncActivateAgent.fulfilled, (state, action) => {
				// Update the agent status in the state (assuming the agent object is updated)
				state.data = state.data.map((agent) => 
					agent.id === action.meta.arg 
						? { ...agent, status: 'active' } 
						: agent
				);
			})
			.addCase(asyncActivateAgent.rejected, (state) => {
				state.isLoading = false;
			})
			.addCase(asyncRetrieveAgentTransactions.pending, (state) => {
				state.isLoading = true;
			})
			.addCase(
				asyncRetrieveAgentTransactions.fulfilled,
				(state, action: PayloadAction<{ data: AgentTransaction[]; pagination: any }>) => {
					state.isLoading = false;
					state.agentTranscation = action.payload.data;
					state.agentTransactionPagination = action.payload.pagination;
				},
			)
			.addCase(asyncRetrieveAgentTransactions.rejected, (state) => {
				state.isLoading = false;
			})
			.addCase(asyncRetrieveAgentFloatRequest.pending, (state) => {
				state.isLoading = true;
			})
			.addCase(
				asyncRetrieveAgentFloatRequest.fulfilled,
				(state, action: PayloadAction<FloatRequest[]>) => {
					state.isLoading = false;
					state.floatRequest = action.payload;
				},
			)
			.addCase(asyncRetrieveAgentFloatRequest.rejected, (state) => {
				state.isLoading = false;
			})
			.addCase(asyncRetrieveAgent.pending, (state) => {
				state.isLoading = true;
			})
			.addCase(asyncRetrieveAgent.fulfilled, (state, action: PayloadAction<AgentInfo>) => {
				state.isLoading = false;
				state.agent = action.payload;
			});
	},
});

export default agentSlice.reducer;
export const selectAgents = (state: RootState) => state.agents;
